/**
 * Tarifas de VT com CATÁLOGO temporal por tipo (rodada 23/09/2026): o tipo de
 * papel "vt" ganha um catálogo de tarifas nomeadas com vigência
 * (GET/POST/PATCH/DELETE /beneficio-tipos/:id/tarifas) e o vínculo passa a
 * referenciar a tarifa DO CATÁLOGO (tarifaCatalogoId) ou seguir com valor livre
 * (tarifaCentavos) — exatamente um dos dois por linha. Helpers puros compartilhados
 * pela aba do funcionário (editor do vínculo) e pelo catálogo (BeneficiosPage).
 */
import { fmtMoeda } from "./format";

/** Tarifa do catálogo do tipo (uma vigência por linha). */
export interface TarifaCatalogo {
  id: string;
  nome: string;
  valorCentavos: number;
  vigenciaInicio: number;
  vigenciaFim?: number | null;
}

/** Linha do editor de tarifas do vínculo: catálogo OU valor livre (nunca os dois). */
export interface TarifaLinhaForm {
  tarifaCatalogoId: string | null;
  /** Valor livre em centavos (linha sem tarifa do catálogo). */
  tarifa: number | null;
  quantidade: number;
}

/** Valor do Select da linha quando ela é de VALOR LIVRE (fora do catálogo). */
export const TARIFA_LIVRE = "__valor_livre__";

/** A linha está preenchida? (catálogo escolhido OU valor livre informado). */
export function tarifaPreenchida(t: TarifaLinhaForm): boolean {
  return t.tarifaCatalogoId !== null || t.tarifa !== null;
}

/** Payload da API da linha: exatamente um de tarifaCatalogoId/tarifaCentavos. */
export function tarifaPayload(t: TarifaLinhaForm): { tarifaCatalogoId: string; quantidade: number } | { tarifaCentavos: number; quantidade: number } {
  return t.tarifaCatalogoId !== null
    ? { tarifaCatalogoId: t.tarifaCatalogoId, quantidade: t.quantidade }
    : { tarifaCentavos: t.tarifa!, quantidade: t.quantidade };
}

/** Linha do editor a partir do item gravado no vínculo (legado = valor livre). */
export function tarifaLinhaDoVinculo(t: { tarifaCentavos: number; tarifaCatalogoId?: string | null; quantidade: number }): TarifaLinhaForm {
  return t.tarifaCatalogoId
    ? { tarifaCatalogoId: t.tarifaCatalogoId, tarifa: null, quantidade: t.quantidade }
    : { tarifaCatalogoId: null, tarifa: t.tarifaCentavos, quantidade: t.quantidade };
}

/** Rótulo da opção do catálogo no Select: "Bilhete único — R$ 5,40". */
export function rotuloTarifaCatalogo(t: TarifaCatalogo): string {
  return `${t.nome} — R$ ${fmtMoeda(t.valorCentavos)}`;
}

/** Uso de uma tarifa do catálogo (GET /beneficio-tipos/:id/tarifas/:tarifaId/usos). */
export interface UsoTarifa {
  funcionarioId: string;
  nome: string;
  matriculaDominio: number | null;
  vinculoId: string;
  vigenciaInicio: number;
  vigenciaFim: number | null;
  quantidade: number;
}

/** Linha de tarifa CONGELADA (valor livre gravado no vínculo, fora do catálogo). */
export interface ItemTarifaSolta {
  tarifaCentavos: number;
  quantidade: number;
}

/** Grupo de tarifas soltas por vínculo (GET /beneficio-tipos/:id/tarifas-soltas)
 *  — o monitor de valores fixados direto no funcionário (deveria esvaziar). */
export interface GrupoTarifaSolta {
  funcionarioId: string;
  nome: string;
  matriculaDominio: number | null;
  vinculoId: string;
  vigenciaInicio: number;
  vigenciaFim: number | null;
  itens: ItemTarifaSolta[];
}

/** Cadeia temporal de tarifas de um MESMO nome: as vigências em ordem e a
 *  vigência ABERTA (vigenciaFim null — a última da cadeia, mesmo que comece
 *  no futuro = "pré-cadastrada"). O vínculo referencia a cadeia (tipoId+nome):
 *  um valor novo vira a vigência seguinte e os vínculos ligados seguem sozinhos. */
export interface CadeiaTarifa {
  nome: string;
  vigencias: TarifaCatalogo[];
  aberta: TarifaCatalogo | null;
}

/** Agrupa vigentes+historico por nome (a cadeia) e ordena as vigências por
 *  vigenciaInicio; cadeias em ordem alfabética. */
export function montarCadeiasTarifas(vigentes: TarifaCatalogo[], historico: TarifaCatalogo[]): CadeiaTarifa[] {
  const porNome = new Map<string, TarifaCatalogo[]>();
  for (const t of [...historico, ...vigentes]) {
    const lista = porNome.get(t.nome) ?? [];
    lista.push(t);
    porNome.set(t.nome, lista);
  }
  return [...porNome.entries()]
    .map(([nome, vigencias]) => {
      const ordenadas = [...vigencias].sort((a, b) => a.vigenciaInicio - b.vigenciaInicio);
      return { nome, vigencias: ordenadas, aberta: ordenadas.find((v) => v.vigenciaFim == null) ?? null };
    })
    .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
}
